import { useLayoutEffect, useState } from 'react'
import { SIDEBAR_SECTION_ATTRIBUTE } from '../../../../shared/utils/sidebarNavigation'
import type { GamerSection } from '../../../../shared/types/navigation'
import {
  TOUR_ARROW_SIZE_PX,
  TOUR_PANEL_GAP_PX,
  TOUR_PANEL_TOP_OFFSET_PX,
  TOUR_SCREEN_GUTTER_PX
} from '../components/platformTourStyles'

export interface TourPanelPlacement {
  anchorRect: DOMRect | null
  top: number
  left: number
  maxWidth: number
  arrowTop: number
  panelRef: (panel: HTMLElement | null) => void
}

const isSameRect = (first: DOMRect | null, second: DOMRect | null) =>
  first?.top === second?.top &&
  first?.left === second?.left &&
  first?.width === second?.width &&
  first?.height === second?.height

/** The menu entry of this section that is on screen: the desktop sidebar, never the closed phone drawer. */
const findVisibleMenuItem = (section: GamerSection): HTMLElement | null => {
  const items = document.querySelectorAll<HTMLElement>(`[${SIDEBAR_SECTION_ATTRIBUTE}="${section}"]`)
  return (
    Array.from(items).find((item) => {
      const rect = item.getBoundingClientRect()
      return rect.width > 0 && rect.left >= 0
    }) ?? null
  )
}

/** Follows the menu entry of a section as the menu grows, shrinks or the window resizes. */
const useMenuItemRect = (section: GamerSection | undefined): DOMRect | null => {
  const [rect, setRect] = useState<DOMRect | null>(null)

  useLayoutEffect(() => {
    if (!section) {
      setRect(null)
      return
    }

    const measure = () => {
      const next = findVisibleMenuItem(section)?.getBoundingClientRect() ?? null
      setRect((previous) => (isSameRect(previous, next) ? previous : next))
    }
    measure()

    const menu = findVisibleMenuItem(section)?.parentElement
    const resizeObserver = new ResizeObserver(measure)
    if (menu) resizeObserver.observe(menu)
    window.addEventListener('resize', measure)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [section])

  return rect
}

/** Where the stop panel sits: beside the lit menu entry, kept inside the window; docked when the menu is hidden. */
export const useTourPanelPlacement = (section: GamerSection | undefined): TourPanelPlacement => {
  const anchorRect = useMenuItemRect(section)
  const [panel, setPanel] = useState<HTMLElement | null>(null)
  const [panelHeight, setPanelHeight] = useState(0)

  useLayoutEffect(() => {
    if (!panel) return

    const resizeObserver = new ResizeObserver(() => setPanelHeight(panel.offsetHeight))
    resizeObserver.observe(panel)
    return () => resizeObserver.disconnect()
  }, [panel])

  if (!anchorRect) return { anchorRect, top: 0, left: 0, maxWidth: 0, arrowTop: 0, panelRef: setPanel }

  const lowestTop = window.innerHeight - panelHeight - TOUR_SCREEN_GUTTER_PX
  const top = Math.max(TOUR_SCREEN_GUTTER_PX, Math.min(anchorRect.top - TOUR_PANEL_TOP_OFFSET_PX, lowestTop))
  const left = anchorRect.right + TOUR_PANEL_GAP_PX
  const anchorMiddle = anchorRect.top + anchorRect.height / 2
  const arrowTop = anchorMiddle - top - TOUR_ARROW_SIZE_PX / 2
  const maxWidth = window.innerWidth - left - TOUR_SCREEN_GUTTER_PX

  return { anchorRect, top, left, maxWidth, arrowTop, panelRef: setPanel }
}
